import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
    Tag,
    Flag,
    Link,
    Image,
    theme,
    Heading2,
    HelpToBuyLogo,
    ButtonWithIcon,
    ButtonLinkWithIcon
} from '@blueprint/ui';
import { HaloType, SubImageComponent } from './ListingCard.types';
import { ButtonPrev, ButtonNext } from '@zoopla/gallery';

type Props = {
    haloType: HaloType;
};

const getRems = (pixels: number) => {
    return `${pixels / 16}rem`;
};

const TransportWrapper = styled.div(css`
    padding: ${theme.spacing['spacing-2']} 0;
`);

const StyledWrapper = styled.div<Props>(
    ({ theme: { colors, breakpoints, borderRadius }, haloType }) => css`
        border-bottom: solid 1px ${colors['graphic-divider']};

        ${haloType === HaloType.PREMIUM &&
        css`
            border-radius: ${borderRadius['8px']} ${borderRadius['8px']} 0 0;
            overflow: hidden;
            background-color: ${colors['white']};
            border: 1px solid;
            border-bottom: 0;
            border-color: ${colors['purple-bright']};
        `}

        @media only screen and (min-width: ${breakpoints[1]}px) {
            display: flex;
        }

        @media only screen and (max-width: ${breakpoints[1]}px) {
            ${haloType === HaloType.FEATURED &&
            css`
                border-radius: ${borderRadius['8px']} ${borderRadius['8px']} 0 0;
                overflow: hidden;
                background-color: ${colors['white']};
                border: 1px solid;
                border-bottom: 0;
                border-color: ${colors['tone-blue-t1']};
            `}
        }
    `
);

const ImageWrapper = styled('div')<Props>(
    ({ theme: { colors, breakpoints, borderRadius }, haloType }) => css`
        width: 100%;
        z-index: 2;
        position: relative;
        background: ${colors['graphic-background-accent']};
        overflow: hidden;
        ${haloType !== HaloType.NONE &&
        css`
            border-radius: ${borderRadius['8px']} 0 0 0;
        `}
        @media only screen and (min-width: ${breakpoints[1]}px) {
            max-width: ${getRems(430)};
        }
        @media only screen and (max-width: ${breakpoints[1]}px) {
            ${haloType !== HaloType.NONE &&
            css`
                border-top-right-radius: ${borderRadius['8px']};
            `}
        }
    `
);

const SubImageContainer = styled(Image)<SubImageComponent>(
    ({ selected, theme: { colors } }) => css`
        width: ${getRems(69)};
        height: ${getRems(50)};
        display: inline-block;
        margin-top: ${theme.spacing['4px']};
        margin-right: ${theme.spacing['4px']};

        border-bottom: ${selected
            ? `solid ${colors['purple-bright']} ${theme.spacing['4px']}`
            : `solid ${colors['white']} ${theme.spacing['4px']}`};
        opacity: ${selected ? 0.65 : 1};

        ${theme.mediaQueries.medium} {
            width: ${getRems(105.25)};
            height: ${getRems(76)};
            cursor: pointer;
        }
    `
);

const SubImageWrapper = styled.div(
    ({ theme: { colors } }) => css`
        display: flex;
        justify-content: center;
        background: ${colors['white']};

        ${SubImageContainer}:last-child {
            margin-right: 0px;
        }
    `
);

const ImageContainer = styled.div(css`
    position: relative;
    width: 100%;
    min-height: ${getRems(207)};
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        max-width: ${getRems(430)};
        min-height: ${getRems(310)};
    }
`);

const ImageCenterWrapper = styled.div(css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    object-fit: contain;
    width: 100%;
    min-height: ${getRems(207)};
    max-height: ${getRems(207)};

    ${theme.mediaQueries.medium} {
        max-width: ${getRems(430)};
        min-height: ${getRems(350)};
        max-height: ${getRems(207)};
    }
`);

const DateDetailWrapper = styled.div(css`
    display: block;
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        display: flex;
    }
`);

const ViewCountContainer = styled.span(css`
    display: block;
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        margin-left: ${theme.spacing['spacing-2']};
        display: flex;
    }
`);

const AvailableFromContainer = ViewCountContainer;

const StyledContent = styled.div(css`
    position: relative;
    flex-grow: 1;

    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        justify-content: center;
        align-items: center;
        margin: 0;
    }
`);

const StyledLink = styled(Link)(css`
    display: block;
    padding: ${theme.spacing['spacing-2']};
    width: 100%;
    height: 100%;

    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        padding: ${theme.spacing['spacing-5']}, ${theme.spacing['spacing-3']};
    }
`);

const BadgeHolder = styled('div')(css`
    position: absolute;
    pointer-events: none;
    margin: ${theme.spacing['spacing-2']} 0 0 ${theme.spacing['spacing-2']};
    top: 0;
`);

const StyledHelpToBuy = styled(HelpToBuyLogo)(css`
    position: absolute;
    right: ${theme.spacing['spacing-2']};
    top: ${theme.spacing['spacing-2']};
`);

const WrapperFeatures = styled.div(css`
    display: flex;
    padding-top: ${theme.spacing['spacing-1']};
`);

const AddressTitle = styled(Heading2)(css`
    padding-top: ${theme.spacing['spacing-1']};
`);

const StyledTagList = styled('ul')(
    ({ theme: { spacing } }) => css`
        list-style: none;
        margin: 0 -${spacing['spacing-1']} -${spacing['spacing-1']} 0;
        padding: 0;
        white-space: nowrap;
        font-size: 0;
    `
);

const StyledTagListItem = styled('li')(
    ({ theme: { spacing } }) => css`
        display: inline-block;
        margin-right: ${spacing['spacing-1']};
        margin-bottom: ${spacing['spacing-1']};
    `
);

const CardFooter = styled.div<Props>(
    ({
        theme: { spacing, colors, borderRadius, breakpoints },
        haloType
    }) => css`
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        padding: ${spacing['spacing-2']};
        align-items: center;

        ${haloType === HaloType.PREMIUM &&
        css`
            background-color: ${colors['white']};
            overflow: hidden;
            border-radius: 0 0 ${borderRadius['8px']} ${borderRadius['8px']};
            border: 1px solid ${colors['purple-bright']};
            border-top: 1px solid ${colors['graphic-divider']};
        `}

        @media only screen and (max-width: ${breakpoints[1]}px) {
            ${haloType === HaloType.FEATURED &&
            css`
                background-color: ${colors['white']};
                overflow: hidden;
                border-radius: 0 0 ${borderRadius['8px']} ${borderRadius['8px']};
                border: 1px solid ${colors['tone-blue-t1']};
                border-top-color: ${colors['graphic-divider']};
            `}
        }
    `
);

const CardFooterCell = styled.div(css`
    display: flex;
    align-items: center;
    z-index: 2;
    ${ButtonWithIcon} {
        padding: 0;
        margin: 0;
        min-width: unset;
    }
`);

// TODO: alias box-shadow to in-page-low
const Wrapper = styled.div<Props>(
    ({
        theme: { mq, borderRadius, boxShadow, colors, spacing, breakpoints },
        haloType
    }) => css`
        border: solid 0 ${theme.colors.white};
        position: relative;
        background-color: ${theme.colors.white};
        border: solid 1px ${theme.colors['graphic-divider']};
        border-radius: ${borderRadius['300']};
        overflow: hidden;

        :hover {
            box-shadow: ${boxShadow['100']};
        }

        ${haloType === HaloType.PREMIUM &&
        css`
            border: none;
            padding: ${spacing['2px']};
            border-radius: ${borderRadius['8px']};
            background-color: ${colors['purple-bright-opacity40']};
        `}

        @media only screen and (max-width: ${breakpoints[1]}px) {
            ${haloType === HaloType.FEATURED &&
            css`
                border: none;
                padding: ${spacing['2px']};
                border-radius: ${borderRadius['8px']};
                background-color: ${colors['tone-blue-t3']};
            `}
        }

        ${ButtonPrev}, ${ButtonNext} {
            display: none;
        }

        ${mq.medium} {
            :hover {
                ${ButtonPrev}, ${ButtonNext} {
                    display: flex;
                }
            }
        }
    `
);

const CardHeader = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
`;

const PriceContainer = styled.div(css`
    ${Flag} {
        margin-bottom: ${theme.spacing['8px']};
        margin-right: ${theme.spacing['4px']};
        float: left;
        clear: both;

        &:last-of-type {
            margin-right: 0;
        }

        ${theme.mq.small} {
            float: none;
        }
    }
`);

const PriceTitleText = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.body};
        color: ${theme.colors['text-body']};
        clear: both;
    `
);

const AgencyLogoLink = styled(Link)(css`
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;
    line-height: 1;
    max-width: ${getRems(73)};
    margin: ${theme.spacing['spacing-2']};

    ${Image} {
        max-height: ${getRems(42)};
        width: auto;
        display: block;
    }

    p {
        text-align: right;
    }
`);

const MediaLinksContainer = styled.div(css`
    position: absolute;
    bottom: ${theme.spacing['spacing-2']};
    right: ${theme.spacing['spacing-1']};
    pointer-events: none;
    ${Tag} {
        display: inline-flex;
        align-items: center;
        margin-right: ${theme.spacing['spacing-1']};
        background-color: ${theme.colors['purple-dark-opacity80']};
        color: ${theme.colors.white};
        svg {
            margin-right: ${theme.spacing['micro-spacing-2']};
        }
    }
`);

const ImageLink = styled(Link)(css`
    display: contents;
`);

const ContactLink = styled(ButtonLinkWithIcon)(
    (props) => css`
        display: none;
        margin: 0 ${props.theme.spacing['24px']} 0 0;
        padding: 0;
        min-width: unset;

        &:focus {
            outline: 0;
            box-shadow: ${props.theme.focusRing};
        }

        ${theme.mq.medium} {
            display: inline-block;
        }
    `
);

const PriceDropText = styled('p')(
    ({ theme }) => css`
        margin: ${theme.spacing['16px']} 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.secondary};
        color: ${theme.colors['grey-slate']};
        clear: both;

        strong {
            font-weight: ${theme.type.semiBold};
            color: ${theme.colors['crimson']};
        }
    `
);

const PageGalleryContent = styled('div')(css`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
`);

const Halo = styled.div<Props>(
    ({ theme: { breakpoints, spacing }, haloType }) => css`
        display: none;
        padding: ${spacing['spacing-1']};
        ${haloType === HaloType.PREMIUM &&
        css`
            display: revert;
            padding: ${spacing['spacing-1']};
        `}
        @media only screen and (max-width: ${breakpoints[1]}px) {
            ${haloType === HaloType.FEATURED &&
            css`
                display: revert;
                padding: ${spacing['spacing-1']};
            `}
        }
    `
);

const Text = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.body};
        color: ${theme.colors['text-body']};
    `
);

const Price = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type['6']};
        color: ${theme.colors['text-body']};
        font-weight: ${theme.type.semiBold};
    `
);

const Rent = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.size8LineHeight};
        font-size: ${theme.type['8']};
        color: ${theme.colors['text-placeholder']};
        font-weight: ${theme.type.semiBold};
    `
);

const Address = styled('p')(
    ({ theme }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.secondary};
        color: ${theme.colors['grey-steel']};
    `
);

const FooterText = styled('span')<{ isBold?: boolean }>(
    ({ theme, isBold }) => css`
        margin: 0;
        line-height: ${theme.type.lineHeight};
        font-size: ${theme.type.small};
        color: ${theme.colors['grey-steel']};
        font-weight: ${isBold ? theme.type.semiBold : theme.type.normal};
    `
);

export {
    TransportWrapper,
    StyledWrapper,
    ImageWrapper,
    ImageContainer,
    ImageCenterWrapper,
    DateDetailWrapper,
    ViewCountContainer,
    BadgeHolder,
    StyledHelpToBuy,
    WrapperFeatures,
    AddressTitle,
    StyledTagList,
    StyledTagListItem,
    CardFooter,
    CardFooterCell,
    Wrapper,
    CardHeader,
    PriceContainer,
    AgencyLogoLink,
    MediaLinksContainer,
    SubImageContainer,
    SubImageWrapper,
    ContactLink,
    ImageLink,
    AvailableFromContainer,
    PriceDropText,
    PriceTitleText,
    PageGalleryContent,
    StyledContent,
    StyledLink,
    Halo,
    Text,
    FooterText,
    Address,
    Price,
    Rent
};
